/**
 * PanelLoadingFallback - shared Suspense skeleton for async demo panels.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import type { MonographDemoTheme } from "./monograph-demo-shell/types.js";
import { LoadingSpinner } from "./LoadingSpinner.js";

type PanelFallbackTheme = Partial<
  Pick<MonographDemoTheme, "border" | "muted" | "surface" | "text" | "mono">
>;

export interface PanelLoadingFallbackProps {
  readonly accentColor?: string;
  readonly label?: string;
  readonly maxWidth?: number;
  readonly minHeight?: number | string;
  readonly theme?: PanelFallbackTheme;
  readonly style?: React.CSSProperties;
}

const DEFAULT_THEME: Required<PanelFallbackTheme> = {
  border: "rgba(148, 163, 184, 0.28)",
  mono: "ui-monospace, SFMono-Regular, Menlo, monospace",
  muted: "#94a3b8",
  surface: "rgba(15, 23, 42, 0.72)",
  text: "#e2e8f0",
};

function themeValue<Key extends keyof PanelFallbackTheme>(
  theme: PanelFallbackTheme | undefined,
  key: Key,
): Required<PanelFallbackTheme>[Key] {
  return theme?.[key] ?? DEFAULT_THEME[key];
}

function SkeletonLine({
  width,
  height,
  color,
  accentColor,
}: {
  readonly width: string;
  readonly height: number;
  readonly color: string;
  readonly accentColor?: string;
}): React.JSX.Element {
  return (
    <div
      aria-hidden="true"
      style={{
        background:
          accentColor === undefined
            ? color
            : `linear-gradient(90deg, ${color} 0%, ${accentColor}33 50%, ${color} 100%)`,
        borderRadius: 6,
        height,
        width,
      }}
    />
  );
}

export function PanelLoadingFallback({
  accentColor = "#38bdf8",
  label = "Loading panel...",
  maxWidth = 1100,
  minHeight = "100vh",
  theme,
  style,
}: PanelLoadingFallbackProps): React.JSX.Element {
  const border = themeValue(theme, "border");
  const mono = themeValue(theme, "mono");
  const muted = themeValue(theme, "muted");
  const surface = themeValue(theme, "surface");
  const text = themeValue(theme, "text");

  return (
    <div
      aria-busy="true"
      style={{
        color: text,
        fontFamily: mono,
        margin: "0 auto",
        maxWidth,
        minHeight,
        padding: "32px 32px 64px",
        ...style,
      }}
    >
      <div style={{ marginBottom: 28 }}>
        <SkeletonLine width="40%" height={26} color={border} accentColor={accentColor} />
        <div style={{ height: 8 }} />
        <SkeletonLine width="22%" height={12} color={border} />
        <div
          aria-hidden="true"
          style={{
            background: `linear-gradient(90deg, ${accentColor}, transparent)`,
            borderRadius: 1,
            height: 2,
            marginTop: 14,
            maxWidth: 260,
            opacity: 0.6,
          }}
        />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        {[0, 1].map((index) => (
          <div
            key={index}
            aria-hidden="true"
            style={{
              background: surface,
              border: `1px solid ${border}`,
              borderRadius: 16,
              display: "flex",
              flexDirection: "column",
              gap: 12,
              padding: 20,
            }}
          >
            <SkeletonLine width="60%" height={14} color={border} accentColor={accentColor} />
            <SkeletonLine width="80%" height={10} color={border} />
            <SkeletonLine width="76%" height={10} color={border} />
            <SkeletonLine width="40%" height={10} color={border} />
          </div>
        ))}
      </div>
      <div style={{ color: muted, marginTop: 18 }}>
        <LoadingSpinner compact color={accentColor} label={label} />
      </div>
    </div>
  );
}
